import React, { useEffect, useMemo, useState } from "react";
import {
  Breadcrumb,
  Button,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Row,
  Col,
} from "antd";
import { EyeOutlined, FormOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";

import { getRequest } from "../../../axios";
import { ShowErrorMsg } from "../../../utils/Notifications";
import "../../../styles/Main.scss";
import SearchIcon from "../../../image/SearchIcon.svg";

const { RangePicker } = DatePicker;
const { Search } = Input;

const STATUS_OPTIONS = [
  "Pending Approval",
  "Approved – Awaiting Shipment",
  "Shipped to Vendor",
  "Closed",
];

const formatDisplayDate = (val) => {
  if (!val) return "—";
  if (typeof val !== "string") return String(val);
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? val : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const cleanQueryParams = (obj) =>
  Object.fromEntries(
    Object.entries(obj || {}).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  );

const ReturnRequestsList = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [status, setStatus] = useState("All");
  const [site, setSite] = useState("All");
  const [dateRange, setDateRange] = useState(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedRa, setSelectedRa] = useState(null);
  const [listPagination, setListPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const fetchReturnRequests = async (queryParams = {}) => {
    try {
      setLoading(true);
      const res = await getRequest("return-requests/", cleanQueryParams(queryParams));
      const payload = res?.data;
      const count = payload?.count ?? 0;
      const results = payload?.results;
      const rawList =
        results?.data ??
        (Array.isArray(results) ? results : []) ??
        payload?.data ??
        (Array.isArray(payload) ? payload : []);
      const list = Array.isArray(rawList) ? rawList : [];
      const mapped = list.map((item) => {
        const raId = item.ra_id ?? item.raId ?? item.id ?? "—";
        const sourceRef = item.source_ref ?? item.source_site ?? item.site_name ?? "—";
        const itemName = item.item_name ?? "—";
        const returnQty = item.return_qty ?? 0;
        const uom = item.uom ?? "";
        const itemsSummary =
          item.items_summary ?? item.itemsSummary ?? (`${itemName} (${returnQty} ${uom})`.trim() || "—");
        const statusVal = item.status ?? "Pending Approval";
        const rejectedByRaw =
          item.rejected_by ??
          item.rejection_by ??
          item.rejectedBy ??
          item.rejected_by_name ??
          item.rejection_by_name ??
          item.rejectedByName ??
          null;
        const rejectedByName =
          rejectedByRaw && typeof rejectedByRaw === "object"
            ? rejectedByRaw?.name ??
              rejectedByRaw?.full_name ??
              rejectedByRaw?.fullName ??
              rejectedByRaw?.username ??
              rejectedByRaw?.first_name ??
              rejectedByRaw?.email ??
              null
            : rejectedByRaw;
        const rejectedByEmail =
          (rejectedByRaw && typeof rejectedByRaw === "object"
            ? rejectedByRaw?.email ??
              rejectedByRaw?.user_email ??
              rejectedByRaw?.mail ??
              null
            : null) ??
          item.rejected_by_email ??
          item.rejection_by_email ??
          item.rejectedByEmail ??
          null;
        const rejectionDateTimeRaw =
          item.rejection_date_time ??
          item.rejection_datetime ??
          item.rejected_at ??
          item.rejection_date ??
          item.rejectedAt ??
          null;
        return {
          key: String(item.id ?? raId),
          id: item.id,
          raId: String(raId),
          sourceSite: sourceRef,
          sourceRef,
          supplier: item.supplier ?? item.supplier_name ?? "—",
          itemName,
          returnQty,
          uom,
          status: statusVal,
          isCompleted: !!item.is_completed,
          rejectedByName: rejectedByName ? String(rejectedByName) : "—",
          rejectedByEmail: rejectedByEmail ? String(rejectedByEmail) : "",
          rejectionDateTime:
            rejectionDateTimeRaw ? String(rejectionDateTimeRaw) : "—",
          rejectionDateTimeRaw,
        };
      });
      setTableData(mapped);
      setTotalCount(count > 0 ? count : mapped.length);
    } catch (err) {
      ShowErrorMsg(err?.response?.data?.error || err?.message || "Failed to load return requests");
      setTableData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setListPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchValue, status, site]);

  useEffect(() => {
    fetchReturnRequests(
      cleanQueryParams({
        page: listPagination.current,
        pagesize: listPagination.pageSize,
        search: searchValue.trim() || undefined,
        status: status === "All" ? undefined : status,
        site: site === "All" ? undefined : site,
      })
    );
  }, [
    listPagination.current,
    listPagination.pageSize,
    searchValue,
    status,
    site,
  ]);

  const siteOptions = useMemo(() => {
    const sites = Array.from(new Set(tableData.map((r) => r.sourceSite).filter(Boolean)));
    return ["All", ...sites];
  }, [tableData]);

  useEffect(() => {
    const len = totalCount;
    const ps = listPagination.pageSize || 10;
    const maxPage = Math.max(1, Math.ceil(len / ps) || 1);
    if (listPagination.current > maxPage) {
      setListPagination((prev) => ({ ...prev, current: maxPage }));
    }
  }, [totalCount, listPagination.pageSize, listPagination.current]);

  const handleListTableChange = (pagination) => {
    setListPagination({
      current: pagination?.current ?? 1,
      pageSize: pagination?.pageSize ?? 10,
    });
  };

  const columns = [
    { title: "RA ID", dataIndex: "raId", key: "raId", },
    { title: "Source Site", dataIndex: "sourceSite", key: "sourceSite", },
    { title: "Supplier", dataIndex: "supplier", key: "supplier",  },
    { title: "Item", dataIndex: "itemName", key: "itemName" },
    { title: "Qty.", dataIndex: "returnQty", key: "returnQty",  align: "right" },
    { title: "UoM.", dataIndex: "uom", key: "uom",  },
    { title: "Status", dataIndex: "status", key: "status",  },
    {
      title: "Rejected By",
      dataIndex: "rejectedByName",
      key: "rejectedByName",
      width: 180,
      render: (val, record) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span>{val ?? "—"}</span>
          {record?.rejectedByEmail ? (
            <Tooltip
              title={`Email: ${record.rejectedByEmail}`}
              placement="topLeft"
            >
              <InfoCircleOutlined style={{ color: "#094e8f", cursor: "pointer" }} />
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    {
      title: "Rejection Date & Time",
      dataIndex: "rejectionDateTime",
      key: "rejectionDateTime",
      width: 180,
      render: (val) => <span>{val ?? "—"}</span>,
    },
    {
      title: "Action",
      key: "action",
      width: 140,
      render: (_, record) => {
        const actions = [];
        actions.push(
          <Tooltip
            key="open"
            title={record.isCompleted ? "View details" : "Review / Take action"}
          >
            <span
              role="button"
              tabIndex={0}
              onClick={() =>
                navigate("/return-authorization-detail", {
                  state: { raId: record.raId, id: record.id },
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/return-authorization-detail", {
                    state: { raId: record.raId, id: record.id },
                  });
                }
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                // borderRadius: 8,
                // border: "1px solid #d9d9d9",
                color: "#094e8f",
                cursor: "pointer",
                background: "#fff",
              }}
            >
              {record.isCompleted ? (
                <EyeOutlined style={{ fontSize: 18 }} />
              ) : (
                <FormOutlined style={{ fontSize: 18 }} />
              )}
            </span>
          </Tooltip>
        );
        if (record.status === "Approved – Awaiting Shipment") {
          actions.push(
            <Button
            type="primary"
              key="print"
              size="small"
              className="SaveGradiantBtn"
              onClick={() => {
                setSelectedRa(record);
                setIsPrintModalOpen(true);
              }}
            >
              Print Label
            </Button>
          );
        }
        if (record.status === "Shipped to Vendor") {
          actions.push(
            <Button
            type="primary"
              key="track"
              size="small"
              className="SaveGradiantBtn"
              onClick={() =>
                navigate("/track-shipment", {
                  state: { raId: record.raId },
                })
              }
            >
              Track Shipment
            </Button>
          );
        }
        return <Space size="small">{actions}</Space>;
      },
    },
  ];

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip title="Track all Return Authorizations across sites and suppliers">
              <h3>Return Request Dashboard (RA List)</h3>
            </Tooltip>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Return Request Dashboard</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
      
            <Spin spinning={loading}>
              <Table
                rowKey="key"
                columns={columns}
                dataSource={tableData}
                onChange={handleListTableChange}
                pagination={{
                  current: listPagination.current,
                  pageSize: listPagination.pageSize,
                  defaultCurrent: listPagination.current,
                  defaultPageSize: listPagination.pageSize,
                  total: totalCount,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                  locale: {
                    items_per_page: "Items / Page",
                  },
                  showTotal: (total, range) =>
                    `Showing ${range[0]}-${range[1]} of ${total} records`,
                }}
                scroll={{ x: 1000 }}
              />
            </Spin>
        </div>
      </section>

      <Modal
        title={`Print Shipping Label – ${selectedRa?.raId || "RA-2025-002"}`}
        open={isPrintModalOpen}
        onCancel={() => setIsPrintModalOpen(false)}
        footer={null}
        width={900}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={14}>
            <div className="tablewrp">
              <div className="table-title">Label Preview</div>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="FROM">
                  {selectedRa?.sourceSite || "Thaltej Site Store, Ahmedabad"}
                </Descriptions.Item>
                <Descriptions.Item label="TO">
                  {selectedRa?.supplier || "Supplier"}
                </Descriptions.Item>
                <Descriptions.Item label="Barcode / QR">
                  <Space>
                    <div className="tablewrp">BARCODE</div>
                    <div className="tablewrp">QR</div>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="RA Reference">
                  RA Ref: {selectedRa?.raId || "RA-2025-002"}
                </Descriptions.Item>
                <Descriptions.Item label="Footer">
                  Package 1 of 1 | Wgt: 55kg
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Col>
          <Col xs={24} md={10}>
            <div className="tablewrp">
              <div className="table-title">Print Settings</div>
              <Form
                layout="vertical"
                initialValues={{
                  printer: "Warehouse Thermal ZEBRA ZT411",
                  labelSize: '4" x 6" Roll',
                  orientation: "Portrait",
                  copies: 1,
                }}
                onFinish={(values) => {
                  setIsPrintModalOpen(false);
                }}
              >
                <Form.Item label="Printer" name="printer" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { label: "Warehouse Thermal ZEBRA ZT411", value: "Warehouse Thermal ZEBRA ZT411" },
                      { label: "Office Laser Printer", value: "Office Laser Printer" },
                      { label: "PDF Export", value: "PDF Export" },
                    ]}
                  />
                </Form.Item>
                <Form.Item label="Label Size" name="labelSize" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { label: '4" x 6" Roll', value: '4" x 6" Roll' },
                      { label: "A4 Sheet", value: "A4 Sheet" },
                    ]}
                  />
                </Form.Item>
                <Form.Item label="Orientation" name="orientation" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { label: "Portrait", value: "Portrait" },
                      { label: "Landscape", value: "Landscape" },
                    ]}
                  />
                </Form.Item>
                <Form.Item label="Copies" name="copies" rules={[{ required: true }]}>
                  <InputNumber min={1} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" className="SaveGradiantBtn" block>
                    Print Label
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ReturnRequestsList;

